class SearchOverlay extends HTMLElement {
  static RECENT_KEY = 'occ_recent_searches';
  static MAX_RECENT = 3;

  constructor() {
    super();
    this.backButton = this.querySelector('[data-search-overlay-close]');
    this.recentSection = this.querySelector('[data-recent-searches]');
    this.recentList = this.querySelector('[data-recent-searches-list]');
    this.popularLinks = this.querySelectorAll('[data-popular-search]');
    this.emptyState = this.querySelector('[data-search-empty-state]');
    this.noResultsSection = this.querySelector('[data-search-no-results]');
    this.noResultsText = this.querySelector('[data-search-no-results-text]');
    this.bestsellersSection = this.querySelector('[data-search-bestsellers]');
    this.resultsSlot = this.querySelector('[data-search-results-slot]');
    this.seeAllLink = this.querySelector('[data-search-see-all]');
    this.seeAllText = this.querySelector('[data-search-see-all-text]');
    this.overlayInput = this.querySelector('.search-overlay__input');
    this.clearButton = this.querySelector('.search-overlay__clear');
    this.boundClose = this.close.bind(this);
    this.boundOnKeydown = this.onKeydown.bind(this);
    this.boundOnDocumentClick = this.onDocumentClick.bind(this);
    this.headerInputDebounceTimers = new WeakMap();
    this.triggerInput = null;
    this.lastResultsTerm = '';
    this.lastResultsTotal = 0;
  }

  connectedCallback() {
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    this.seeAllLink?.addEventListener('click', () => {
      this.saveRecentSearch(this.overlayInput?.value);
    });
    this.backButton?.addEventListener('click', this.boundClose);
    this.popularLinks.forEach((link) => {
      link.addEventListener('click', () =>
        this.saveRecentSearch(link.dataset.popularSearch, { skipResultsCheck: true })
      );
    });
    this.clearButton?.addEventListener('click', () => {
      window.setTimeout(() => this.syncHeaderInputs(''), 0);
    });
    this.bindSearchTriggers();
    this.renderRecentSearches();
    this.highlightPopularMatches('');
    this.updateResultsVisibility(this.overlayInput?.value || '');
  }

  disconnectedCallback() {
    this.backButton?.removeEventListener('click', this.boundClose);
    document.removeEventListener('keydown', this.boundOnKeydown);
    document.removeEventListener('click', this.boundOnDocumentClick);
  }

  bindSearchTriggers() {
    document
      .querySelectorAll('.header__search button[aria-controls="SearchOverlay"]')
      .forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(null);
        });
      });

    document
      .querySelectorAll('.header__search input[type="search"], main-search input[type="search"]')
      .forEach((input) => {
      if (input.closest('search-overlay')) return;

      input.addEventListener('input', () => {
        const existingTimer = this.headerInputDebounceTimers.get(input);
        if (existingTimer) window.clearTimeout(existingTimer);

        const debouncedTimer = window.setTimeout(() => {
          const trimmedValue = input.value.trim();
          if (!trimmedValue) return;
          this.open(input);
        }, 300);

        this.headerInputDebounceTimers.set(input, debouncedTimer);
      });

      input.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(input);
      });

      input.form?.addEventListener('submit', (event) => {
        if (!this.hasAttribute('open')) return;
        const term = this.formatSearchTerm(input.value);
        if (!term) return;
        input.value = term;
        this.syncHeaderInputs(term);
        this.saveRecentSearch(term);
      });
    });

  }

  getPredictiveSearch() {
    return this.querySelector('predictive-search[data-overlay-mode]');
  }

  open(triggerInput) {
    const value = triggerInput?.value || this.overlayInput?.value || '';
    const triggerDetails = triggerInput?.closest('details');
    if (triggerDetails?.hasAttribute('open')) {
      triggerDetails.removeAttribute('open');
    }

    if (triggerInput) {
      this.triggerInput = triggerInput;
    }

    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.boundOnKeydown);
    // Ignore the opening gesture (focus + click) so the same tap does not immediately close.
    this.ignoreDocumentClickUntil = Date.now() + 300;
    window.setTimeout(() => {
      if (this.hasAttribute('open')) {
        document.addEventListener('click', this.boundOnDocumentClick);
      }
    }, 0);

    if (this.overlayInput) {
      this.overlayInput.value = value;
      this.toggleClearButton(value);
    }

    this.syncHeaderInputs(value);
    this.renderRecentSearches();
    this.highlightPopularMatches(value);
    this.updateResultsVisibility(value);
    this.focusOverlayInput(value);

    if (value.trim().length) {
      this.triggerPredictiveSearch(value);
    }

    this.initProductSliders();
  }

  focusOverlayInput(value = '') {
    const panel = this.querySelector('.search-overlay__panel');
    const input = this.overlayInput;
    if (!input) return;

    let selectionStart = input.value.length;
    let selectionEnd = input.value.length;

    // Preserve caret position from the trigger without blurring it first —
    // blur() drops user activation on mobile and can hide the caret/keyboard.
    if (this.triggerInput && this.triggerInput !== input && typeof this.triggerInput.selectionStart === 'number') {
      selectionStart = this.triggerInput.selectionStart;
      selectionEnd = this.triggerInput.selectionEnd;
    }

    // Focus immediately within the user gesture so mobile keyboards open.
    try {
      input.focus({ preventScroll: true });
      input.setSelectionRange(selectionStart, selectionEnd);
    } catch {
      // Some browsers reject focus or setSelectionRange during overlay transitions.
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (typeof trapFocus === 'function' && panel) {
          trapFocus(panel, input);
        } else if (document.activeElement !== input) {
          input.focus({ preventScroll: true });
        }

        try {
          input.setSelectionRange(selectionStart, selectionEnd);
        } catch {
          // Some browsers reject setSelectionRange on certain input states.
        }
      });
    });
  }

  triggerPredictiveSearch(term) {
    const trimmed = term?.trim();
    if (!trimmed) return;

    const predictiveSearch = this.getPredictiveSearch();
    if (!predictiveSearch) return;

    predictiveSearch.searchTerm = trimmed;
    predictiveSearch.getSearchResults(trimmed);
  }

  close() {
    const triggerInput = this.triggerInput;
    this.triggerInput = null;

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(triggerInput || null);
    }

    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.boundOnKeydown);
    document.removeEventListener('click', this.boundOnDocumentClick);

    document.querySelectorAll('.header__search predictive-search').forEach((instance) => {
      if (!instance.hasAttribute('data-overlay-mode')) instance.close(true);
    });
  }

  onKeydown(event) {
    if (event.code === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  onDocumentClick(event) {
    if (!this.hasAttribute('open')) return;
    if (Date.now() < (this.ignoreDocumentClickUntil || 0)) return;

    if (event.target.closest('.search-overlay__panel')) return;

    if (
      event.target.closest(
        'button[aria-controls="SearchOverlay"], .header__search input[type="search"], main-search input[type="search"], drawer-search, .menu-drawer__search'
      )
    ) {
      return;
    }

    this.close();
  }

  getSearchUrl(term) {
    return `${window.routes?.search_url || '/search'}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
  }

  getSyncSearchInputs() {
    return document.querySelectorAll(
      '.header__search input[type="search"], main-search input[type="search"], drawer-search input[type="search"]'
    );
  }

  syncHeaderInputs(value) {
    this.getSyncSearchInputs().forEach((input) => {
      if (input === this.overlayInput) return;
      input.value = value;
      const resetButton = input.form?.querySelector('button[type="reset"]');
      resetButton?.classList.toggle('hidden', !value.length);
    });
  }

  isSearchPage() {
    return Boolean(document.querySelector('.template-search'));
  }

  getCurrentSearchTerm() {
    const overlayTerm = this.overlayInput?.value?.trim();
    if (overlayTerm) return overlayTerm;

    if (this.isSearchPage()) {
      const urlTerm = new URLSearchParams(window.location.search).get('q')?.trim();
      if (urlTerm) return urlTerm;
    }

    for (const input of this.getSyncSearchInputs()) {
      const value = input.value?.trim();
      if (value) return value;
    }

    return '';
  }

  matchesCurrentSearchTerm(term) {
    const formatted = this.formatSearchTerm(term);
    const current = this.formatSearchTerm(this.getCurrentSearchTerm());
    return Boolean(formatted) && formatted.toLowerCase() === current.toLowerCase();
  }

  clearCurrentSearch() {
    if (this.isSearchPage()) {
      const url = new URL(window.location.href);
      if (!url.searchParams.get('q')?.trim()) return;

      url.searchParams.delete('q');
      const query = url.searchParams.toString();
      window.location.replace(query ? `${url.pathname}?${query}` : url.pathname);
      return;
    }

    if (this.overlayInput) {
      this.overlayInput.value = '';
    }
    this.toggleClearButton('');
    this.syncHeaderInputs('');
    this.updateResultsVisibility('');
    this.highlightPopularMatches('');

    this.getPredictiveSearch()?.close(true);
    document.querySelectorAll('.header__search predictive-search:not([data-overlay-mode])').forEach((instance) => {
      instance.close(true);
    });
  }

  toggleClearButton(value) {
    this.clearButton?.classList.toggle('hidden', !value?.length);
  }

  getRecentSearches() {
    try {
      const recent = JSON.parse(localStorage.getItem(SearchOverlay.RECENT_KEY)) || [];
      return recent.slice(0, SearchOverlay.MAX_RECENT);
    } catch {
      return [];
    }
  }

  formatSearchTerm(term) {
    const trimmed = term?.trim();
    if (!trimmed) return '';

    return trimmed
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  saveRecentSearch(term) {
    const formatted = this.formatSearchTerm(term);
    if (!formatted) return;

    const recent = this.getRecentSearches().filter((item) => item.toLowerCase() !== formatted.toLowerCase());
    recent.unshift(formatted);
    localStorage.setItem(SearchOverlay.RECENT_KEY, JSON.stringify(recent.slice(0, SearchOverlay.MAX_RECENT)));
    this.renderRecentSearches();
  }

  removeRecentSearch(term) {
    const formatted = this.formatSearchTerm(term);
    if (!formatted) return;

    const matchesCurrent = this.matchesCurrentSearchTerm(formatted);

    const recent = this.getRecentSearches().filter((item) => item.toLowerCase() !== formatted.toLowerCase());
    localStorage.setItem(SearchOverlay.RECENT_KEY, JSON.stringify(recent));
    this.renderRecentSearches();

    if (matchesCurrent) {
      this.clearCurrentSearch();
    }
  }

  renderRecentSearches() {
    if (!this.recentList || !this.recentSection) return;

    const recent = this.getRecentSearches();
    this.recentList.innerHTML = '';

    if (!recent.length) {
      this.recentSection.hidden = true;
      return;
    }

    this.recentSection.hidden = false;
    recent.forEach((term) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      const removeButton = document.createElement('button');
      link.href = this.getSearchUrl(term);
      link.className = 'search-overlay__sidebar-link link';
      link.dataset.recentSearch = term;
      link.textContent = term;
      removeButton.type = 'button';
      removeButton.className = 'search-overlay__recent-remove';
      removeButton.setAttribute('aria-label', `Remove ${term} from recent searches`);
      removeButton.dataset.recentRemove = term;
      const clearIcon = this.clearButton?.querySelector('.svg-wrapper');
      if (clearIcon) {
        removeButton.appendChild(clearIcon.cloneNode(true));
      }
      removeButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.removeRecentSearch(term);
      });
      item.appendChild(link);
      item.appendChild(removeButton);
      this.recentList.appendChild(item);
    });

    this.highlightPopularMatches(this.overlayInput?.value || '');
  }

  highlightPopularMatches(query) {
    const normalized = query.trim().toLowerCase();

    this.popularLinks.forEach((link) => {
      const term = link.dataset.popularSearch?.toLowerCase() || '';
      link.classList.toggle('is-active', normalized.length > 0 && term.includes(normalized));
    });

    this.recentList?.querySelectorAll('[data-recent-search]').forEach((link) => {
      const term = link.dataset.recentSearch?.toLowerCase() || '';
      link.classList.toggle('is-active', normalized.length > 0 && term.includes(normalized));
    });
  }

  showNoResultsMessage(term) {
    if (!this.noResultsSection || !this.noResultsText) return;

    const template = window.searchOverlayStrings?.noResults;
    const escapedTerm = String(term)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

    this.noResultsText.innerHTML = template
      ? template.replace('[terms]', escapedTerm)
      : `No results for “<strong>${escapedTerm}</strong>”`;
    this.noResultsSection.hidden = false;
  }

  hideNoResultsMessage() {
    if (!this.noResultsSection) return;
    this.noResultsSection.hidden = true;
  }

  mountResults(markup, fullSearchCount = null) {
    if (!this.resultsSlot) return;

    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const results = parsed.querySelector('#predictive-search-results');
    const countNode = parsed.querySelector('[data-predictive-search-live-region-count-value]');

    const queryTerm = this.overlayInput?.value?.trim() || '';
    const hasQuery = Boolean(queryTerm.length);
    const predictiveTotal = Number(countNode?.dataset.totalResults ?? NaN);
    const displayTotal =
      typeof fullSearchCount === 'number' && !Number.isNaN(fullSearchCount) ? fullSearchCount : predictiveTotal;
    const hasResults = Boolean(results) && !Number.isNaN(predictiveTotal) && predictiveTotal > 0;
    const noResults = hasQuery && Boolean(results) && !Number.isNaN(predictiveTotal) && predictiveTotal === 0;

    this.lastResultsTerm = queryTerm;
    this.lastResultsTotal = Number.isNaN(displayTotal) ? 0 : displayTotal;

    this.resultsSlot.innerHTML = results ? results.outerHTML : markup;
    this.resultsSlot.querySelectorAll('.predictive-search__loading-state').forEach((spinner) => spinner.remove());
    this.resultsSlot.hidden = !hasResults;

    const showBestsellers = !hasQuery || noResults;

    if (noResults) {
      this.showNoResultsMessage(queryTerm);
    } else {
      this.hideNoResultsMessage();
    }

    if (this.bestsellersSection) {
      this.bestsellersSection.hidden = !showBestsellers;
    }
    if (this.emptyState) {
      this.emptyState.hidden = showBestsellers || hasResults || Boolean(this.bestsellersSection);
    }

    if (hasResults) {
      const resultTotal = Number.isNaN(displayTotal) ? 0 : displayTotal;
      this.updateProductsHeading(resultTotal);
    }

    if (this.seeAllLink && this.overlayInput?.value) {
      const searchUrl = this.getSearchUrl(this.overlayInput.value);
      this.seeAllLink.href = searchUrl;
      const resultTotal = Number.isNaN(displayTotal) ? 0 : displayTotal;
      this.seeAllLink.hidden = noResults || !hasResults || resultTotal <= 0;
      if (this.seeAllText && window.searchOverlayStrings?.seeAllResults) {
        this.seeAllText.textContent = window.searchOverlayStrings.seeAllResults.replace('[count]', String(resultTotal));
      }
    }

    this.highlightPopularMatches(this.overlayInput?.value || '');
    this.initProductSliders();
  }

  updateProductsHeading(resultTotal) {
    if (!this.resultsSlot || !window.searchOverlayStrings?.productsHeading || resultTotal <= 0) return;

    const productsHeading = this.resultsSlot.querySelector('#predictive-search-products-heading');
    if (!productsHeading) return;

    productsHeading.textContent = `${window.searchOverlayStrings.productsHeading.replace('[count]', String(resultTotal))} - `;
  }

  initProductSliders() {
    window.requestAnimationFrame(() => {
      this.querySelectorAll('slider-component').forEach((slider) => {
        if (typeof slider.resetPages === 'function') {
          slider.resetPages();
        }
      });
    });
  }

  updateResultsVisibility(query) {
    const hasQuery = Boolean(query?.trim().length);

    if (!hasQuery) {
      this.lastResultsTerm = '';
      this.lastResultsTotal = 0;
      if (this.resultsSlot) {
        this.resultsSlot.innerHTML = '';
        this.resultsSlot.hidden = true;
      }
      this.hideNoResultsMessage();
      if (this.bestsellersSection) this.bestsellersSection.hidden = false;
      if (this.emptyState) this.emptyState.hidden = Boolean(this.bestsellersSection);
      if (this.seeAllLink) this.seeAllLink.hidden = true;
      return;
    }

    this.hideNoResultsMessage();
    if (this.emptyState) this.emptyState.hidden = Boolean(this.bestsellersSection);
  }

  onOverlayInput(term) {
    this.toggleClearButton(term);
    this.syncHeaderInputs(term);
    this.highlightPopularMatches(term);
    if (!term.trim().length) this.updateResultsVisibility('');
  }

  onSearchSubmit(term) {
    const formatted = this.formatSearchTerm(term);
    if (this.overlayInput && formatted) {
      this.overlayInput.value = formatted;
      this.syncHeaderInputs(formatted);
    }
    this.saveRecentSearch(formatted);
    this.close();
  }
}

customElements.define('search-overlay', SearchOverlay);
